/**
 * Passives — the read side of `{ kind: 'passive' }` (issue #4, docs/05 §4).
 *
 * Six unlocks granted a passive and **nothing queried `unlockedPassives()`**, so
 * `codex.hint.1` fired at 10 discoveries, toasted "Unlocked — codex.hint.1", and
 * the codex was byte-identical afterwards. The failure mode is the one issue #2
 * catalogued one level up: a string id with no reader looks exactly like a
 * string id with a reader.
 *
 * The shape of the fix is *resolve once, read everywhere*. A passive is a
 * property of the profile, not of the frame, so walking the unlock set on every
 * codex render would be both wasteful and a second place to get the id spelling
 * wrong. `resolvePassives` collapses the set into a flags object at run start
 * and every consumer reads a field:
 *
 *   ash-carry-20      -> `bankRun`        (meta/runState.ts)
 *   map-echo          -> `bankRun` + `createRunState` (meta/runState.ts)
 *   show-composition  -> `redactEnemyEntry`  (called by core/uiAdapter.ts)
 *   hint-reagent/     -> `reactionHint`      (called by core/uiAdapter.ts)
 *   hint-conditions/
 *   hint-chains
 *
 * The two codex passives are *redaction* rather than *construction* on purpose.
 * The adapter already builds composition and weakness rows; asking it to build
 * them conditionally would put the unlock check inside a loop that has no other
 * reason to know about progression. Building them always and emptying them here
 * keeps the gate in one line, in one file, and makes it testable without a
 * renderer — which is what §7 of docs/13 asks for.
 *
 * Decoupled like its neighbours: no imports from combat/, sim/, ui/ or core/.
 * Enemy and reaction rows arrive as structural shapes.
 */

import { UNLOCKS } from './progression';
import type { Progression } from './progression';

export type PassiveId =
  | 'ash-carry-20'
  | 'map-echo'
  | 'show-composition'
  | 'hint-reagent'
  | 'hint-conditions'
  | 'hint-chains';

/**
 * Every passive the unlock table grants, in unlock order.
 *
 * Derived rather than restated: a seventh passive added to `UNLOCKS` appears
 * here without an edit, and `tests/unlocks.test.ts` asserts every id here is one
 * `resolvePassives` actually reads, so a passive that arrives without a consumer
 * fails the suite instead of granting nothing (which is this issue, again).
 */
export const PASSIVE_IDS: readonly string[] = UNLOCKS.filter(
  (u) => u.grants.kind === 'passive',
).map((u) => (u.grants as { passive: string }).passive);

/**
 * Fraction of unbanked ash that survives a death, with Ash Courier held.
 *
 * The unlock's own copy says 20%, so the constant is 0.2 and the copy is the
 * spec. Note what this does *not* change: `ASH_ON_DEATH` still banks 55% of the
 * run's ash into the permanent balance. Courier is a separate 20% that rides
 * into the next run's pocket, so the two compose to a converging series rather
 * than a doubling — a chain of deaths banks 0.55/(1−0.2) ≈ 69% of what was
 * picked up, not 75% and not infinity.
 */
export const ASH_CARRY_FRACTION = 0.2;

export interface RunPassives {
  /** Fraction of unbanked ash carried through death. 0 without Ash Courier. */
  ashCarry: number;
  /** The room graph of a failed run replays into the next run on the same seed. */
  mapEcho: boolean;
  /** The codex may show enemy composition, and the weaknesses derived from it. */
  showComposition: boolean;
  /**
   * 0 none · 1 names the reagent you are missing · 2 adds the condition ·
   * 3 predicts a chain. Levels rather than three booleans because the three
   * unlocks are a strict ramp (`hint.2` requires `hint.1`), and a level makes
   * "at least this much" the natural test at every call site.
   */
  hintLevel: 0 | 1 | 2 | 3;
}

/** What a profile with no passives has. Frozen: it is shared, never mutated. */
export const NO_PASSIVES: RunPassives = Object.freeze({
  ashCarry: 0,
  mapEcho: false,
  showComposition: false,
  hintLevel: 0,
});

/** The projection, from raw passive ids. Exported for tests and for `resolvePassives`. */
export function passivesFromIds(ids: Iterable<string>): RunPassives {
  const held = new Set(ids);
  const hintLevel: 0 | 1 | 2 | 3 = held.has('hint-chains')
    ? 3
    : held.has('hint-conditions')
      ? 2
      : held.has('hint-reagent')
        ? 1
        : 0;
  return {
    ashCarry: held.has('ash-carry-20') ? ASH_CARRY_FRACTION : 0,
    mapEcho: held.has('map-echo'),
    showComposition: held.has('show-composition'),
    hintLevel,
  };
}

/** The one call site of `Progression.unlockedPassives()`. */
export function resolvePassives(prog: Progression): RunPassives {
  return passivesFromIds(prog.unlockedPassives());
}

// `describePassives` lived here and was deleted by #118's sweep. It formatted a
// `RunPassives` for "the run log and the progression screen", and neither
// surface has ever existed: the run log prints `describeGrants`, which already
// counts passives, and there is no progression screen. A formatter for a
// display that was never built is the same shape as `progressionSummary`, which
// went the same way, and re-authoring six lines of `join` is cheaper than
// carrying them past a third sweep that has to judge them again.

// ------------------------------------------------------- composition autopsy

/**
 * The structural half of `EnemyEntryVM`. Declared here rather than imported so
 * this file keeps its "no ui/ imports" rule; `EnemyEntryVM` satisfies it.
 */
export interface EnemyDisclosureLike {
  composition: unknown[];
  weaknesses: unknown[];
  resistances: unknown[];
}

/**
 * Empty an enemy entry's derived rows unless Composition Autopsy is held.
 *
 * Mutates in place, matching the adapter's pooled-view-model convention — the
 * codex rebuilds into the same objects every revision and allocating a redacted
 * copy per enemy per rebuild would undo that.
 *
 * Composition is the *cause* and weaknesses/resistances are its consequence
 * (docs/03 §6, `resolve.ts` `typeEffectivenessVs`), so hiding one and showing
 * the other would leak the whole system while pretending not to. Returns true
 * when something was withheld, so a panel can say "locked" rather than render a
 * blank section that reads as missing data.
 */
export function redactEnemyEntry(entry: EnemyDisclosureLike, p: RunPassives): boolean {
  if (p.showComposition) return false;
  entry.composition.length = 0;
  entry.weaknesses.length = 0;
  entry.resistances.length = 0;
  return true;
}

/** The line a panel shows in place of a redacted composition block. */
export const AUTOPSY_LOCK_HINT =
  'Composition hidden — unlock Composition Autopsy (Codex 20) to read what this is made of.';

// -------------------------------------------------------------- reaction hints

/**
 * The structural half of a reaction rule. `ReactionEntryVM` and the raw rule
 * table both satisfy it, so the adapter can pass whichever it already has.
 */
export interface ReactionRuleLike {
  /** The two reactants, as display names or material ids — whatever the caller shows. */
  a: string;
  b: string;
  /** "above 400°C", "on contact" — the rule's own condition prose. */
  condition: string;
  /** What the pair produces. Used for chain prediction at level 3. */
  outcome: string;
}

export interface HintContext {
  /** Materials the codex has recorded — `UIAdapter.materialsSeen`, in its own vocabulary. */
  seen: ReadonlySet<string>;
  /** True once this reaction has actually been observed. */
  discovered: boolean;
  /** The full rule table, for level-3 chain prediction. Optional; level 3 is a no-op without it. */
  rules?: readonly ReactionRuleLike[];
}

/**
 * What the codex is allowed to say about a reaction the player has not seen.
 *
 * Each level answers a strictly harder question, which is what makes the ramp
 * worth 10 / 40 / 90 Codex rather than three copies of the same reveal:
 *
 *   1. *which reagent am I missing* — only answerable when exactly one side is
 *      already recorded. A rule with neither side recorded returns null: the
 *      player has no thread to pull, and naming both materials would hand over
 *      the discovery rather than hint at it.
 *   2. *under what conditions* — the interesting case is both reagents recorded
 *      and the reaction still unseen, which is precisely "I have tried this and
 *      nothing happened".
 *   3. *what happens next* — a chain the outcome feeds. This one applies to
 *      reactions already discovered, because a chain is a fact about a product
 *      you have made, not a nudge toward one you have not.
 *
 * Null means "say nothing", never "say something empty".
 */
export function reactionHint(
  rule: ReactionRuleLike,
  ctx: HintContext,
  p: RunPassives,
): string | null {
  if (p.hintLevel === 0) return null;

  if (!ctx.discovered) {
    const haveA = ctx.seen.has(rule.a);
    const haveB = ctx.seen.has(rule.b);
    if (haveA !== haveB) return `Missing reagent: ${haveA ? rule.b : rule.a}.`;
    if (!haveA && !haveB) return null;
    // Both recorded and still unobserved — the condition is the whole answer.
    if (p.hintLevel >= 2) return `Both reagents recorded — needs ${rule.condition}.`;
    return null;
  }

  if (p.hintLevel < 3 || !ctx.rules) return null;
  const next = predictChain(rule, ctx.rules);
  return next ? `Chain: ${rule.outcome} + ${next.other} → ${next.outcome}.` : null;
}

/**
 * One step past a known reaction: a rule whose input is this rule's product.
 *
 * Deliberately one step and not a search. "Chains of length 3" counts the
 * reaction the player is looking at as the first link, so `A+B → C`, `C+D → E`
 * is already three materials deep; a graph walk would be a solver, and a codex
 * that solves the material system is the end of the material system.
 *
 * Self-loops are skipped — a rule that consumes its own product would predict
 * an infinite chain and tell the player nothing.
 */
function predictChain(
  rule: ReactionRuleLike,
  rules: readonly ReactionRuleLike[],
): { other: string; outcome: string } | null {
  for (const r of rules) {
    if (r === rule) continue;
    if (r.outcome === rule.outcome) continue;
    if (r.a === rule.outcome && r.b !== rule.outcome) return { other: r.b, outcome: r.outcome };
    if (r.b === rule.outcome && r.a !== rule.outcome) return { other: r.a, outcome: r.outcome };
  }
  return null;
}

// ------------------------------------------------------------------ map echo
//
// The persisted half lives in `meta/mapEcho.ts`, which imports nothing — see
// that file's header for why it cannot live here.

// `unlockForPassive` and `passiveDescription` lived here and were deleted by
// #118's sweep — a pair, and dead as a pair: the first had exactly one caller,
// which was the second, and the second had none. They existed to put a price on
// a locked passive ("Locked — reach 40 Codex"), and no panel shows passives at
// all, locked or held. Every other lock hint in the game is built where it is
// drawn, off `UNLOCKS` directly — `runSetupAdapter`'s `KIT_UNLOCK`,
// `worldModifiers`' `MODIFIER_UNLOCK`, `unlockEffects`' archetype rows — so a
// passives panel would build its own the same way rather than reach for this.
